import React, { useState } from 'react';
import Layout from '../components/Layout';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';
import { useChartData } from '../hooks/useReports';
import { useSettingsQuery } from '../hooks';
import { Loader2, Timer, CalendarRange } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../contexts/AuthContext';
import { useShiftEligibility } from '../hooks/useShiftSchedules';
import { AccountingSummary } from '../components/AccountingSummary';
import { todayInTz } from '../utils/timezoneUtils';
import { getAppTimezone } from '../services/dateService';

const Reports = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  
  // Vérifier l'éligibilité de planification
  const { data: shiftEligibility } = useShiftEligibility();
  const isWithinScheduledHours = isAdmin || shiftEligibility?.is_eligible;

  const tz = getAppTimezone();
  const today = todayInTz(tz);
  const firstOfMonth = `${today.slice(0, 8)}01`;
  const [range, setRange] = useState({ start: firstOfMonth, end: today });

  const { salesData, chartData, isLoading } = useChartData(range);
  const { data: settings } = useSettingsQuery();

  const periodLabel = `${range.start} → ${range.end}`;

  // Fonction de formatage des montants avec 2 décimales max
  const formatAmount = (amount) => {
    const currency = settings?.currency || 'GNF';
    const symbols = { USD: '$', CAD: '$ CAD', EUR: '€', XOF: 'FCFA', GNF: 'GNF' };
    const decimals = { USD: 2, CAD: 2, EUR: 2, XOF: 0, GNF: 0 };
    const dec = decimals[currency] ?? 2;
    const formatted = (amount || 0).toLocaleString('fr-FR', { 
      minimumFractionDigits: 0, 
      maximumFractionDigits: dec 
    });
    return `${formatted} ${symbols[currency] || currency}`;
  };

  return (
    <Layout>
      <div className="space-y-8" data-testid="reports-page">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
          <div>
            <h1 className="text-4xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Rapports
            </h1>
            <p className="text-slate-600" style={{ fontFamily: 'Inter, sans-serif' }}>
              Statistiques et analyses de votre pharmacie
            </p>
          </div>
          {isWithinScheduledHours && (
            <div className="flex items-end gap-2 p-3 bg-white rounded-xl border border-slate-200" data-testid="reports-period-selector">
              <CalendarRange className="w-5 h-5 text-teal-600 mb-2" strokeWidth={1.5} />
              <div>
                <Label htmlFor="rp-start" className="text-xs">Du</Label>
                <Input id="rp-start" type="date" value={range.start} max={today} onChange={(e) => setRange({ ...range, start: e.target.value })} className="h-9" data-testid="reports-start-date" />
              </div>
              <div>
                <Label htmlFor="rp-end" className="text-xs">Au</Label>
                <Input id="rp-end" type="date" value={range.end} max={today} onChange={(e) => setRange({ ...range, end: e.target.value })} className="h-9" data-testid="reports-end-date" />
              </div>
            </div>
          )}
        </div>

        {/* Message de restriction pour utilisateurs hors horaires */}
        {!isWithinScheduledHours ? (
          <div className="p-6 bg-amber-50 rounded-xl border border-amber-200">
            <div className="flex items-start gap-4">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Timer className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <h3 className="font-semibold text-amber-800">Accès restreint - Hors horaires de travail</h3>
                <p className="text-sm text-amber-700 mt-1">
                  {shiftEligibility?.reason || 'Vous ne pouvez pas accéder aux rapports en dehors de vos horaires planifiés.'}
                </p>
                {shiftEligibility?.schedule && (
                  <p className="text-sm text-amber-600 mt-2">
                    <strong>Horaires prévus :</strong> {shiftEligibility.schedule.start_time} - {shiftEligibility.schedule.end_time}
                  </p>
                )}
                {shiftEligibility?.current_time && (
                  <p className="text-xs text-amber-500 mt-1">
                    Heure actuelle : {shiftEligibility.current_time}
                  </p>
                )}
              </div>
            </div>
          </div>
        ) : isLoading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
          </div>
        ) : (
          <>
            {isAdmin && <AccountingSummary formatAmount={formatAmount} range={range} />}
            {salesData && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="p-6 rounded-2xl bg-white border border-slate-200 shadow-sm">
                  <p className="text-sm text-slate-500 mb-2" style={{ fontFamily: 'Inter, sans-serif' }}>
                    Total des ventes <span className="text-xs text-slate-400">({periodLabel})</span>
                  </p>
                  <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {salesData.total_sales}
                  </p>
                </div>
                <div className="p-6 rounded-2xl bg-white border border-slate-200 shadow-sm">
                  <p className="text-sm text-slate-500 mb-2" style={{ fontFamily: 'Inter, sans-serif' }}>
                    Revenu total <span className="text-xs text-slate-400">({periodLabel})</span>
                  </p>
                  <p className="text-3xl font-bold text-teal-700" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {formatAmount(salesData.total_revenue)}
                  </p>
                </div>
                <div className="p-6 rounded-2xl bg-white border border-slate-200 shadow-sm">
                  <p className="text-sm text-slate-500 mb-2" style={{ fontFamily: 'Inter, sans-serif' }}>
                    Moyenne par vente
                  </p>
                  <p className="text-3xl font-bold text-emerald-700" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {formatAmount(salesData.total_sales > 0 ? salesData.total_revenue / salesData.total_sales : 0)}
                  </p>
                </div>
              </div>
            )}

            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
              <h3 className="text-xl font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Chiffre d'affaires <span className="text-sm font-normal text-slate-400">({periodLabel})</span>
              </h3>
              {chartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={400}>
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="date" stroke="#64748b" style={{ fontFamily: 'Inter, sans-serif' }} />
                    <YAxis stroke="#64748b" style={{ fontFamily: 'Inter, sans-serif' }} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#fff',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        fontFamily: 'Inter, sans-serif',
                      }}
                      formatter={(value) => [formatAmount(value), 'Revenu']}
                    />
                    <Line type="monotone" dataKey="revenue" stroke="#0F766E" strokeWidth={2} dot={{ fill: '#0F766E' }} />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[400px] flex items-center justify-center text-slate-400">
                  Aucune donnée disponible
                </div>
              )}
            </div>

            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
              <h3 className="text-xl font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Nombre de ventes quotidiennes <span className="text-sm font-normal text-slate-400">({periodLabel})</span>
              </h3>
              {chartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="date" stroke="#64748b" style={{ fontFamily: 'Inter, sans-serif' }} />
                    <YAxis stroke="#64748b" style={{ fontFamily: 'Inter, sans-serif' }} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#fff',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        fontFamily: 'Inter, sans-serif',
                      }}
                      formatter={(value) => [value, 'Ventes']}
                    />
                    <Bar dataKey="count" fill="#10B981" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[400px] flex items-center justify-center text-slate-400">
                  Aucune donnée disponible
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </Layout>
  );
};

export default Reports;
